// Phase NAATI-MAX B2 — /en/translate/naati/interpreting
import { createFileRoute } from "@tanstack/react-router";
import NaatiKnowledgePage from "@/components/NaatiKnowledgePage";
import { NAATI_INTERPRETING_SERVICES } from "@/data/naati-interpreting";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const URL = `${SITE}/en/translate/naati/interpreting`;

const faqs = [
  { q: "What level is required in Australian public hospitals?", a: "CSHI (Certified Specialist Health Interpreter) or higher is required. Public hospitals, community health services, and Mental Health Tribunals reject lower-level NAATI interpreters and refuse to admit them into consultations or hearings under national safety standards." },
  { q: "What level is required in Australian courts?", a: "CSLI (Certified Specialist Legal Interpreter) or higher is required across Magistrates', District, Federal, Family, and Federal Circuit courts, as well as the AAT. Judges routinely stand down proceedings if the booked interpreter cannot show a current CSLI credential." },
  { q: "Hourly or per-job?", a: "Hourly with a 2-hour minimum. Travel time is billed for assignments outside Sydney, Melbourne, and Brisbane metro, and long-distance jobs include accommodation and per-diem separately. Half-day and full-day block bookings unlock lower blended rates." },
  { q: "How far in advance to book?", a: "CSHI/CSLI interpreters should be booked 2–4 weeks ahead; CCI (Conference) interpreters 6–8 weeks because the pool is scarce nationally. Urgent same-week bookings are possible for emergency hospital or bail-court matters, subject to availability surcharge." },
];

export const Route = createFileRoute("/en/translate/naati/interpreting")({
  head: () => ({
    meta: [
      { title: ctaTitle("NAATI Certified Interpreting (Thai-English) Australia | NYC", "/en/translate/naati/interpreting", "en")},
      { name: "description", content: ctaDesc("Thai-English NAATI interpreters across Australia — court (CSLI), health (CSHI), business (CI). Get your timeline and checklist in minutes — chat now.", "/en/translate/naati/interpreting", "en")},
      { property: "og:title", content: ctaTitle("NAATI Certified Interpreting (Thai-English) Australia | NYC", "/en/translate/naati/interpreting", "en")},
      { property: "og:description", content: ctaDesc("Thai-English NAATI interpreters across Australia — court (CSLI), health (CSHI), business (CI), conference (CCI). From ~AUD 135/hr, 2-hour min.", "/en/translate/naati/interpreting", "en")},
      { property: "og:url", content: URL },
    ],
    links: canonicalAndHreflang("/translate/naati/interpreting", "/en/translate/naati/interpreting"),
    scripts: [
      { type: "application/ld+json", children: JSON.stringify({
        "@context": "https://schema.org", "@type": "Service",
        "@id": `${URL}#service`, name: "NAATI Certified Interpreting (Thai-English)",
        inLanguage: "en", serviceType: "Certified Interpreting",
        areaServed: { "@type": "Country", name: "Australia" },
        provider: { "@type": "ProfessionalService", name: "NYC Language Institute", url: SITE },
        offers: {
          "@type": "AggregateOffer", priceCurrency: "THB",
          lowPrice: Math.min(...NAATI_INTERPRETING_SERVICES.map((s) => s.hourlyFromThb)),
          highPrice: Math.max(...NAATI_INTERPRETING_SERVICES.map((s) => s.hourlyFromThb)),
          offerCount: NAATI_INTERPRETING_SERVICES.length,
          availability: "https://schema.org/InStock",
        },
      }) },
      { type: "application/ld+json", children: JSON.stringify({
        "@context": "https://schema.org", "@type": "FAQPage", inLanguage: "en",
        mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      }) },
      { type: "application/ld+json", children: JSON.stringify({
        "@context": "https://schema.org", "@type": "WebPage", "@id": `${URL}#speakable`, url: URL, name: "NAATI Certified Translation",
        speakable: { "@type": "SpeakableSpecification", cssSelector: ["[data-speakable]", "h1", ".direct-answer"] },
      }) },
    ],
  }),
  component: () => (
    <NaatiKnowledgePage
      locale="en"
      eyebrow="NAATI Interpreting Services"
      h1="NAATI Certified Thai-English Interpreters Across Australia"
      lead="NAATI interpreters work in courts, public hospitals, police stations, Centrelink, solicitor offices, and at national conferences. Our roster covers CI to CCI across major Australian cities."
      speakableAnswer="Australian courts require CSLI or higher. Public hospitals require CSHI. National conferences require CCI. Rates start at THB 2,500 per hour with a 2-hour minimum."
      sections={[
        {
          heading: "4 interpreting service categories",
          table: {
            headers: ["Service", "Required level", "From (THB/hr)", "Minimum"],
            rows: NAATI_INTERPRETING_SERVICES.map((s) => [
              `${s.nameEn}\n${s.domainEn}`,
              s.requiredLevel,
              `฿${s.hourlyFromThb.toLocaleString()}`,
              `${s.minHours} hours`,
            ]),
          },
        },
        ...NAATI_INTERPRETING_SERVICES.map((s) => ({
          heading: s.nameEn,
          paragraph: `Required level: NAATI ${s.requiredLevel}. From THB ${s.hourlyFromThb.toLocaleString()}/hour (${s.minHours}-hour minimum).`,
          bullets: s.useCasesEn,
        })),
        {
          heading: "Booking workflow",
          bullets: [
            "Send the brief — type, venue, date, duration",
            "We match an interpreter by level + domain experience within 24 hours",
            "Confirm with a 50% deposit to lock the slot",
            "Day of: interpreter arrives 15 minutes early + sends a post-assignment report",
          ],
        },
      ]}
      faqs={faqs}
      cta={{ label: "Book a NAATI interpreter", href: "/en/translate/naati" }}
    />
  ),
});
